// Core entity model shared by server and web.
// Every entity lives in a named collection and is synced through the generic op log
// (see shared/src/sync.ts). Keep fields JSON-serializable.

export type ID = string;
export type Millis = number;

export interface BaseEntity {
  id: ID;
  createdAt: Millis;
  updatedAt?: Millis;
}

export interface User extends BaseEntity {
  name: string;
  email: string;
  color: string; // avatar background when no image
  avatarUrl?: string | null;
  title?: string;
  /** custom status message set from the account menu ("In a meeting", "🌴 On leave") */
  status?: string | null;
  /** the status clears itself at this time (null / absent: until it's cleared) */
  statusUntil?: Millis | null;
  /** when they last had Atrium open (set by the server) */
  lastActiveAt?: Millis | null;
}

/** Roles besides the owner (Workspace.ownerId). People in memberIds without a role are members. */
export type WorkspaceRole = 'admin' | 'guest';

/** What sharing an item grants a guest, on the item and everything inside it. */
export type SharePermission = 'view' | 'comment' | 'edit' | 'full';

/** Guests an item is shared with; members and admins see everything anyway. */
export type Shares = Record<ID, SharePermission>;

export interface Workspace extends BaseEntity {
  name: string;
  color: string;
  /** uploaded photo / logo shown instead of the letter avatar (a /files/w/<workspace>/… path) */
  logoUrl?: string | null;
  memberIds: ID[];
  ownerId: ID;
  /** admins and guests (everyone else is a member); replaced whole on write */
  roles?: Record<ID, WorkspaceRole>;
  /** who invited each person and when they joined (set by the server) */
  joined?: Record<ID, { by: ID | null; at: Millis }>;
}

/** Status type drives icon, grouping ("Not started / Active / Done / Closed") and completion. */
export type StatusType = 'open' | 'unstarted' | 'custom' | 'done' | 'closed';

export interface Status {
  id: ID;
  name: string;
  color: string;
  type: StatusType;
  orderIndex: number;
}

/** A link saved on a space's Overview. */
export interface Bookmark {
  id: ID;
  title: string;
  url: string;
  createdBy: ID;
  createdAt: Millis;
}

/** A file shared on a space's Overview (uploaded like any workspace file). */
export interface SpaceResource {
  id: ID;
  name: string;
  url: string;
  mime: string;
  size?: number;
  createdBy: ID;
  createdAt: Millis;
}

export interface Space extends BaseEntity {
  workspaceId: ID;
  name: string;
  color: string;
  icon?: string | null; // emoji or icon name (ICONS key); null => letter avatar
  private: boolean;
  orderIndex: number;
  statuses: Status[];
  archived?: boolean;
  description?: string;
  /** Overview cards; replaced whole on write */
  bookmarks?: Bookmark[];
  resources?: SpaceResource[];
  /** the space's Overview view has been added once (a deleted Overview isn't re-added) */
  overviewAdded?: boolean;
}

export interface Folder extends BaseEntity {
  spaceId: ID;
  name: string;
  color?: string | null;
  orderIndex: number;
  /** null => inherit from space */
  statuses: Status[] | null;
  archived?: boolean;
  shares?: Shares;
}

export interface List extends BaseEntity {
  spaceId: ID;
  folderId: ID | null;
  name: string;
  color?: string | null;
  orderIndex: number;
  /** null => inherit from folder, then space */
  statuses: Status[] | null;
  description?: string;
  archived?: boolean;
  defaultTaskTypeId?: ID | null;
  /** list-level planning fields, edited from the space Overview */
  startDate?: Millis | null;
  dueDate?: Millis | null;
  priority?: Priority | null;
  ownerId?: ID | null;
  shares?: Shares;
}

export type Priority = 1 | 2 | 3 | 4; // urgent, high, normal, low

export interface TaskType extends BaseEntity {
  workspaceId: ID;
  name: string;
  namePlural: string;
  icon: string; // icon name (ICONS key in web/src/components/ui/Icon.tsx)
  color?: string | null;
  builtin?: 'task' | 'milestone';
  orderIndex: number;
}

export interface ChecklistItem {
  id: ID;
  name: string;
  resolved: boolean;
  assigneeId?: ID | null;
  orderIndex: number;
}

export interface Checklist {
  id: ID;
  name: string;
  orderIndex: number;
  items: ChecklistItem[];
}

export interface Task extends BaseEntity {
  workspaceId: ID;
  listId: ID;
  parentId: ID | null;
  name: string;
  /** TipTap/HTML string */
  description: string;
  statusId: ID;
  assigneeIds: ID[];
  watcherIds: ID[];
  priority: Priority | null;
  startDate: Millis | null;
  dueDate: Millis | null;
  /** whether the due date carries a specific time */
  dueTime?: boolean;
  timeEstimate: number | null; // ms
  tagIds: ID[];
  customFields: Record<ID, CustomFieldValue>;
  taskTypeId: ID | null;
  orderIndex: number;
  createdBy: ID;
  closedAt: Millis | null;
  archived?: boolean;
  checklists: Checklist[];
  points?: number | null;
  shares?: Shares;
}

export type CustomFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'money'
  | 'dropdown'
  | 'labels'
  | 'date'
  | 'checkbox'
  | 'people'
  | 'url'
  | 'email'
  | 'phone'
  | 'rating'
  | 'progress';

export interface FieldOption {
  id: ID;
  name: string;
  color: string;
  orderIndex: number;
}

export type CustomFieldValue = string | number | boolean | ID[] | null;

export interface CustomField extends BaseEntity {
  workspaceId: ID;
  /** where the field is defined; it applies to all lists below that location */
  scope: { type: 'workspace' | 'space' | 'folder' | 'list'; id: ID };
  name: string;
  type: CustomFieldType;
  options?: FieldOption[];
  config?: { currency?: string; precision?: number; ratingMax?: number; ratingEmoji?: string };
  orderIndex: number;
}

export interface Tag extends BaseEntity {
  spaceId: ID;
  name: string;
  color: string;
}

export type LinkType = 'blocking' | 'linked';

/** fromTaskId blocks toTaskId (blocking) or the two are related (linked). */
export interface TaskLink extends BaseEntity {
  fromTaskId: ID;
  toTaskId: ID;
  type: LinkType;
}

export interface Attachment extends BaseEntity {
  taskId: ID;
  name: string;
  size: number;
  mime: string;
  url: string;
  createdBy: ID;
}

export interface Comment extends BaseEntity {
  taskId: ID;
  parentId: ID | null; // thread replies
  authorId: ID;
  body: string; // HTML
  assigneeId?: ID | null; // assigned comment
  resolved?: boolean;
  reactions?: Record<string, ID[]>;
  editedAt?: Millis | null;
}

export type ActivityKind =
  | 'created'
  | 'status'
  | 'name'
  | 'assignee_add'
  | 'assignee_remove'
  | 'priority'
  | 'due_date'
  | 'start_date'
  | 'tag_add'
  | 'tag_remove'
  | 'moved'
  | 'attachment'
  | 'subtask_add'
  | 'description'
  | 'custom_field'
  | 'time_estimate'
  | 'task_type'
  | 'archived'
  | 'dependency';

export interface Activity extends BaseEntity {
  taskId: ID;
  actorId: ID;
  kind: ActivityKind;
  from?: unknown;
  to?: unknown;
  meta?: Record<string, unknown>;
}

export type NotificationKind = 'assigned' | 'mention' | 'comment' | 'status' | 'due_date' | 'priority' | 'reply' | 'blocked' | 'assigned_comment';

export interface Notification extends BaseEntity {
  userId: ID;
  taskId: ID | null;
  actorId: ID;
  kind: NotificationKind;
  /** pre-rendered snippet data */
  data?: Record<string, unknown>;
  bucket: 'primary' | 'other';
  readAt: Millis | null;
  clearedAt: Millis | null;
  laterAt: Millis | null;
}

export type ViewType = 'list' | 'board' | 'calendar' | 'table' | 'gantt' | 'doc' | 'chat' | 'overview';

export type LocationType = 'workspace' | 'space' | 'folder' | 'list';

export interface Location {
  type: LocationType;
  id: ID;
}

export type GroupBy =
  | 'status'
  | 'assignee'
  | 'priority'
  | 'dueDate'
  | 'taskType'
  | 'tags'
  | 'list'
  | 'none'
  | `cf:${string}`;

export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'any'
  | 'all'
  | 'none'
  | 'set'
  | 'not_set'
  | 'contains'
  | 'gt'
  | 'lt'
  | 'today'
  | 'overdue'
  | 'this_week'
  | 'next_week';

export interface FilterRule {
  id: ID;
  field: string; // 'status' | 'assignee' | 'priority' | 'dueDate' | 'tags' | 'taskType' | 'name' | `cf:${id}`
  operator: FilterOperator;
  value?: unknown;
}

export interface FilterGroup {
  op: 'and' | 'or';
  rules: FilterRule[];
}

export interface ViewColumn {
  id: string; // field id: 'name' | 'assignee' | ... | `cf:${id}`
  width?: number;
  hidden?: boolean;
}

export interface ViewSettings {
  groupBy: GroupBy;
  groupDir: 'asc' | 'desc';
  sort: { field: string; dir: 'asc' | 'desc' }[];
  filters: FilterGroup;
  subtasks: 'collapsed' | 'expanded' | 'separate';
  showClosed: boolean;
  meMode: boolean;
  columns: ViewColumn[];
  collapsedGroups: string[];
  calendarMode?: 'month' | 'week' | 'day';
  ganttZoom?: 'day' | 'week' | 'month';
  showEmptyStatuses?: boolean;
}

export interface View extends BaseEntity {
  location: Location;
  type: ViewType;
  name: string;
  orderIndex: number;
  settings: ViewSettings;
  /** doc views point at a doc */
  docId?: ID | null;
  createdBy: ID;
}

export interface Favorite extends BaseEntity {
  userId: ID;
  target: { type: 'space' | 'folder' | 'list' | 'task' | 'view' | 'doc' | 'channel'; id: ID };
  orderIndex: number;
}

export interface TimeEntry extends BaseEntity {
  taskId: ID;
  userId: ID;
  start: Millis;
  end: Millis | null; // null => running
  description?: string;
}

export interface Doc extends BaseEntity {
  workspaceId: ID;
  location: Location | null;
  title: string;
  icon?: string | null;
  createdBy: ID;
  archived?: boolean;
  shares?: Shares;
}

export interface DocPage extends BaseEntity {
  docId: ID;
  parentPageId: ID | null;
  title: string;
  icon?: string | null;
  content: string; // HTML
  orderIndex: number;
  updatedBy?: ID;
}

export interface Channel extends BaseEntity {
  workspaceId: ID;
  kind: 'channel' | 'dm';
  name: string;
  description?: string;
  /** short line shown in the channel settings panel */
  topic?: string;
  private: boolean;
  memberIds: ID[];
  location?: Location | null;
  createdBy: ID;
}

export interface Message extends BaseEntity {
  channelId: ID;
  authorId: ID;
  parentId: ID | null;
  body: string; // HTML
  reactions?: Record<string, ID[]>;
  editedAt?: Millis | null;
  /** pinned to the channel (Pinned panel) */
  pinnedAt?: Millis | null;
  pinnedBy?: ID | null;
}

/** Per-user read markers for chat channels. */
/** One person's read position and notification settings in a channel or DM (personal, synced across devices). */
export interface ChannelRead extends BaseEntity {
  userId: ID;
  channelId: ID;
  lastReadAt: Millis;
  /** what raises a notification: every new message (the default), or only mentions and replies in threads you follow */
  notify?: 'all' | 'mentions';
  /** no notifications from this conversation until then (MUTE_FOREVER: until unmuted) */
  mutedUntil?: Millis | null;
  /** the sidebar section (ChatSection) I've put this conversation in; none = Channels / Direct Messages */
  sectionId?: ID | null;
}

/** A personal group of conversations in my Chat sidebar ("Clients", "Internal"…), like Slack's sections. */
export interface ChatSection extends BaseEntity {
  userId: ID;
  workspaceId: ID;
  name: string;
  orderIndex: number;
}

export interface Collections {
  users: User;
  workspaces: Workspace;
  spaces: Space;
  folders: Folder;
  lists: List;
  taskTypes: TaskType;
  tasks: Task;
  customFields: CustomField;
  tags: Tag;
  taskLinks: TaskLink;
  attachments: Attachment;
  comments: Comment;
  activities: Activity;
  notifications: Notification;
  views: View;
  favorites: Favorite;
  timeEntries: TimeEntry;
  docs: Doc;
  docPages: DocPage;
  channels: Channel;
  messages: Message;
  channelReads: ChannelRead;
  chatSections: ChatSection;
}

export type CollectionName = keyof Collections;

export const COLLECTIONS: CollectionName[] = [
  'users',
  'workspaces',
  'spaces',
  'folders',
  'lists',
  'taskTypes',
  'tasks',
  'customFields',
  'tags',
  'taskLinks',
  'attachments',
  'comments',
  'activities',
  'notifications',
  'views',
  'favorites',
  'timeEntries',
  'docs',
  'docPages',
  'channels',
  'messages',
  'channelReads',
  'chatSections',
];
